import React, { useEffect } from 'react';
import { useSection } from '@/app/providers';
import { SearchableSelect } from '@/core/ui/components/SearchableSelect';

interface SectionMultiSelectProps {
  /** Secciones seleccionadas. Vacío = recurso de todo el residencial. */
  value: string[];
  onChange: (sectionIds: string[]) => void;
  /** Nombre del recurso para el texto de ayuda ("amenidad", "comunicado"...). */
  resourceLabel?: string;
  /** Sustituye el texto de ayuda para el conjunto vacío. */
  emptyHint?: string;
  isDisabled?: boolean;
}

/**
 * Alcance multi-sección de un recurso (§6.5): un comunicado, encuesta,
 * documento, proyecto o amenidad puede pertenecer a varias secciones a la vez.
 *
 * No se renderiza si el tenant no tiene secciones, igual que SectionSelector.
 *
 * Un SECTION_ADMIN recibe del backend un catálogo ya acotado a sus secciones,
 * así que solo puede marcar esas; y como el backend prohíbe el conjunto vacío
 * (= global) a un admin acotado, aquí se preselecciona su sección activa y no
 * puede desmarcar la última.
 */
export const SectionMultiSelect: React.FC<SectionMultiSelectProps> = ({
  value,
  onChange,
  resourceLabel = 'recurso',
  emptyHint,
  isDisabled,
}) => {
  const { sections, hasSections, isSectionAdmin, selectedSectionId } = useSection();

  useEffect(() => {
    if (isSectionAdmin && value.length === 0 && selectedSectionId) onChange([selectedSectionId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSectionAdmin, value.length, selectedSectionId]);

  if (!hasSections) return null;

  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500">
        {isSectionAdmin
          ? sections.length <= 1
            ? `Solo puedes publicar en tu sección.`
            : `Solo puedes publicar en tus secciones; al menos una.`
          : emptyHint ?? `Sin secciones seleccionadas = ${resourceLabel} para todo el residencial.`}
      </p>
      <SearchableSelect
        isMulti
        options={sections.map((section) => ({ value: section.id, label: section.name }))}
        value={value}
        onChange={(next) => {
          if (isSectionAdmin && next.length === 0) return; // no puede quitar la última
          onChange(next);
        }}
        placeholder={isSectionAdmin ? 'Selecciona secciones...' : 'Todo el residencial'}
        isClearable={!isSectionAdmin}
        isDisabled={isDisabled}
      />
    </div>
  );
};
